import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContactService } from 'services';
import { FormDataType } from 'pages/membership';
import { PaymentPlanInstalmentType } from 'type';
import { instalmentHelpers } from './instalmentHelpers';

const useGapPayment = (
  newInstalments: PaymentPlanInstalmentType[] | undefined,
  missedInstalments: PaymentPlanInstalmentType[],
) => {
  const { watch } = useFormContext<FormDataType>();
  const myMembershipsData = useContactService((s) => s.myMemberships.data);
  const membership = myMembershipsData?.[0];

  const overduePlan = watch('overduePlan');
  const { overdueInstalmentsAmount } =
    instalmentHelpers.getOverdueInstalmentsData(((overduePlan && overduePlan?.instalments) || missedInstalments)?.filter(i => i.status === 'Overdue' || i.status === 'Catchup'));

  const missedInstalmentsAmount = useMemo(() => {
    return missedInstalments.reduce((acc, i) => {
      return acc + i.amountIncGst;
    }, 0);
  }, [missedInstalments]);

  const gapPaymentNotice = useMemo(() => {
    const isAutoRenew = membership?.autoRenew;
    const isCurrentSeason = membership?.seasonStatus === 'Current';
    const anyHasMissed = newInstalments?.some((i) => i.status === 'Overdue') || missedInstalments?.some((i) => i.status === 'Overdue');
    return overduePlan ||
      (isCurrentSeason && anyHasMissed) ||
      (isCurrentSeason && !isAutoRenew)
      ? 'overdue'
      : 'catchUp';
  }, [overduePlan, newInstalments, membership]);

  const gapPaymentAmount = useMemo(() => {
    return missedInstalmentsAmount;
  }, [missedInstalmentsAmount]);

  return {
    gapPaymentNotice,
    gapPaymentAmount,
    overdueInstalmentsAmount,
  } as const;
};

const useNextPayment = (
  selectedInstalments: PaymentPlanInstalmentType[],
  pendingInstalments: PaymentPlanInstalmentType[],
) => {
  return useMemo(() => {
    const uniqueInstalments = selectedInstalments.filter(
      (instalment, index, self) =>
        self.findIndex(i => i.id === instalment.id) === index
    );
    return uniqueInstalments.length
      ? uniqueInstalments
        .filter(i => i.status !== 'Paid')
        .reduce((acc, i) => acc + i.amountIncGst, 0)
      : pendingInstalments[0]?.amountIncGst;
  }, [selectedInstalments, pendingInstalments]);
};

export const paymentHelpers = {
  useGapPayment,
  useNextPayment,
};
